import { ORDER_TYPE } from '@exam-management/common/utils';
import {
  FilterOperator,
  QConfigGeneratorExecutionStatus,
  useSearchQConfigGeneratorsQuery,
} from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import { type GridEventListener, type GridRowParams } from '@shared/components';

import { BaseQConfigGeneratorList, type QConfigGenerator } from '../../components';
import { useQConfigGeneratorListSearchParams } from '../../hooks';
import { QConfigGeneratorListNoRowsOverlay } from '../q-config-generator-list-no-rows-overlay';

export default function QConfigGeneratorList() {
  const { page, pageSize, field, sortOrder, changePageNumber, changePageSize, changeSorting } =
    useQConfigGeneratorListSearchParams();
  const { courseId } = useParams('/exam-courses/:courseId/qconfig-generators');
  const navigate = useNavigate();

  const { data, loading: isLoading } = useSearchQConfigGeneratorsQuery({
    variables: {
      searchQConfigGeneratorsInput: {
        filter: {
          conditions: [
            {
              field: 'examCourseId',
              operator: FilterOperator.Equal,
              value: courseId,
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        order: {
          field,
          type: ORDER_TYPE[sortOrder],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const rows: QConfigGenerator[] =
    data?.excSearchQConfigGenerators.qConfigGenerators.map((qConfigGenerator) => ({
      id: qConfigGenerator.id,
      name: qConfigGenerator.name,
      status: qConfigGenerator.latestExecution?.status,
      code: qConfigGenerator.code,
      count: [
        QConfigGeneratorExecutionStatus.Pending,
        QConfigGeneratorExecutionStatus.Processing,
      ].includes(qConfigGenerator.latestExecution?.status)
        ? null
        : qConfigGenerator.latestExecution?.totalResults,
      createAt: qConfigGenerator.latestExecution?.createdAt
        ? new Date(qConfigGenerator.latestExecution.createdAt)
        : undefined,
    })) ?? [];

  const total = data?.excSearchQConfigGenerators.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<QConfigGenerator>,
  ) => {
    navigate('/exam-courses/:courseId/qconfig-generators/:qConfigGeneratorId', {
      params: {
        courseId,
        qConfigGeneratorId: params.row.id,
      },
    });
  };

  return (
    <BaseQConfigGeneratorList
      rows={rows}
      loading={isLoading}
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: QConfigGeneratorListNoRowsOverlay,
      }}
      slotProps={{
        footer: {
          total: total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = Number.parseInt(event.target.value);

              changePageSize(pageSize);
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const field = sorting[0]?.field ?? 'createdAt';
        const sort = sorting[0]?.sort ?? 'desc';
        changeSorting({ sortOrder: sort, sortBy: field });
      }}
    />
  );
}
